import { Link } from 'react-router-dom';
import { track, FUNNEL } from '../../lib/track';
import { GUIDES, GUIDE_TOOLS, BLOG_SLUG, guidePath } from '../../data/guides';

// The pieces every AC guide (/blog/<slug>) is built from, in the order of
// GUIDES_PLAYBOOK.md section 3 (Saheb, 19 Oct 2026: answer first, then a
// simple animation, then pros and cons in bullets, then any tool at the
// bottom; plain headings; mobile first).
//
//   GuideHeader  breadcrumb, H1, byline, date
//   AnswerBox    the one bold answer sentence and up to three "because" bullets
//   Explainer    the card around a guide's animation: plain title, Replay,
//                captions that change as it plays and stay listed under it
//   SimpleTable  "your situation" against "what to choose", six rows at most
//   AnswerGrid   the floor x hours grid, one word in each cell
//   ProsCons     pros and cons of each choice, or a Do and Don't list
//   Parts        "explained simply": plain questions with short bullets
//   Questions    questions people ask, answered in one to three sentences
//   ToolCard     the white card the one tool at the bottom sits in
//   GuideSources, GuideSeries, GuideCta
//
// Each guide's data file supplies a SOURCES list of { key, label, by, href };
// footnotes are numbered in that order.

// **bold** in the guide data files becomes real bold here, and [words](/path)
// a link to another page of the site.
export function Rich({ text }) {
  if (!text) return null;
  return String(text).split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(\/[^)]*\))/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="font-bold text-inherit">{part.slice(2, -2)}</strong>;
    const m = part.match(/^\[([^\]]+)\]\((\/[^)]*)\)$/);
    if (m) return <Link key={i} to={m[2]} className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-2 hover:text-blue-800">{m[1]}</Link>;
    return <span key={i}>{part}</span>;
  });
}

// Footnote links: <Cite keys={['daikin-inverter']} sources={SOURCES} />,
// shown as one superscript, numbers separated by commas.
export function Cite({ keys = [], sources }) {
  if (!keys || !keys.length) return null;
  const ns = [...new Set(keys.map((k) => sources.findIndex((s) => s.key === k) + 1).filter(Boolean))];
  if (!ns.length) return null;
  return (
    <sup className="ml-0.5 whitespace-nowrap">
      {ns.map((n, i) => (
        <span key={n}>
          {i > 0 && <span className="text-[12px] text-gray-400">,</span>}
          <a href={`#src-${n}`} className="px-px text-[12px] font-bold text-blue-700 no-underline hover:text-blue-800" aria-label={`Source ${n}`}>{n}</a>
        </span>
      ))}
    </sup>
  );
}

// The author of every guide (Saheb, 17 Oct 2026), linked to his LinkedIn, the
// same profile as his card on the homepage (src/mock.js).
export const AUTHOR = { name: 'Saheb Sareen', url: 'https://www.linkedin.com/in/sahebsareen/', photo: '/media/hjgs5aq5-image.webp', role: 'Citi Cool, South Extension Part-2' };

const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const CARD = 'rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(17,24,39,0.04),0_8px_24px_rgba(17,24,39,0.05)]';

export function GuideHeader({ seo, updated, crumb }) {
  return (
    <div className="grid gap-3 pt-3.5">
      <nav aria-label="Breadcrumb" className="flex flex-wrap gap-1.5 text-[13px] text-gray-500">
        <Link to="/" className="hover:text-blue-600">Home</Link><span aria-hidden="true">›</span>
        <Link to={`/${BLOG_SLUG}`} className="hover:text-blue-600">AC Guides</Link><span aria-hidden="true">›</span>
        <span className="text-gray-600">{crumb}</span>
      </nav>
      <h1 className="text-[27px] font-extrabold leading-tight tracking-tight text-gray-900 sm:text-[38px]" style={{ textWrap: 'balance' }}>{seo.h1}</h1>
      <div className="flex items-center gap-3">
        <img src={AUTHOR.photo} alt="" width="44" height="44" className="h-11 w-11 flex-none rounded-full bg-gray-100 object-cover" />
        <div className="grid text-[13.5px] leading-snug text-gray-600">
          <span>By <a href={AUTHOR.url} target="_blank" rel="author noopener noreferrer" className="font-semibold text-gray-900 underline decoration-gray-300 underline-offset-2 hover:text-blue-700">{AUTHOR.name}</a></span>
          <span>Updated {fmtDate(updated)}{seo.readMins ? ` · ${seo.readMins} min read` : ''}</span>
        </div>
      </div>
    </div>
  );
}

export function Section({ id, title, lede, children }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="scroll-mt-20 pt-9">
      <h2 id={`${id}-h`} className="text-[21px] font-extrabold leading-snug tracking-tight text-gray-900 sm:text-[26px]" style={{ textWrap: 'balance' }}>{title}</h2>
      {lede && <div className="mt-2 max-w-[64ch] text-gray-600">{lede}</div>}
      {children}
    </section>
  );
}

// THE ANSWER: one bold sentence (or two) that tells the reader what to do,
// and at most three short "because" bullets. On a 400 px phone it sits on
// the first screen with the question.
//   answer: { text, src }   because: [{ text, src }]
export function AnswerBox({ answer, because = [], sources }) {
  return (
    <section aria-labelledby="ans-h" className={`mt-4 p-4 sm:p-5 ${CARD}`}>
      <h2 id="ans-h" className="flex items-center gap-2 text-[12.5px] font-bold uppercase tracking-widest text-blue-700">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>
        The answer
      </h2>
      <p className="mt-2 text-[18px] font-bold leading-snug text-gray-900 sm:text-[20px]"><Rich text={answer.text} /><Cite keys={answer.src} sources={sources} /></p>
      {because.length > 0 && (
        <ul className="mt-3 grid gap-1.5 text-[16px] leading-normal text-gray-700">
          {because.map((b) => (
            <li key={b.text} className="grid grid-cols-[14px_minmax(0,1fr)] gap-2">
              <span aria-hidden="true" className="mt-[9px] h-1.5 w-1.5 rounded-full bg-gray-400" />
              <span><Rich text={b.text} /><Cite keys={b.src} sources={sources} /></span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// THE MAIN ANIMATION'S CARD. The animation itself is the child; `story` comes
// from useStory() (./useStory.js). Captions change as it plays and all stay
// listed under it, the current one marked.
//   captions: [{ at: 0..1, text }]
export function Explainer({ id = 'watch', title, captions = [], story, note, noteSrc = [], sources, level = 2, children }) {
  const H = level === 3 ? 'h3' : 'h2';
  const t = story ? story.t : 1;
  let active = 0;
  captions.forEach((c, i) => { if (t >= c.at) active = i; });
  return (
    <section id={id} aria-labelledby={`${id}-h`} ref={story ? story.ref : undefined} className={`mt-5 scroll-mt-20 overflow-hidden ${CARD}`}>
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <H id={`${id}-h`} className="text-[16px] font-extrabold leading-snug text-gray-900 sm:text-[18px]">{title}</H>
        {story && (
          <button type="button" onClick={story.replay}
            className="inline-flex min-h-[44px] flex-none items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-[14px] font-bold text-gray-900 hover:border-blue-300 hover:text-blue-700">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
            Replay
          </button>
        )}
      </div>
      <div className="grid gap-3 p-3 sm:p-4">
        {children}
        {captions.length > 0 && (
          <ol className="grid gap-1">
            {captions.map((c, i) => {
              const on = i === active;
              return (
                <li key={c.text} className={`grid grid-cols-[26px_minmax(0,1fr)] items-start gap-2.5 rounded-xl px-2 py-1.5 ${on ? 'bg-gray-50' : ''}`}>
                  <span className={`grid h-[26px] w-[26px] place-items-center rounded-full text-[13px] font-extrabold ${on ? 'bg-blue-600 text-white' : i < active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</span>
                  <span className={`text-[15px] leading-snug ${on ? 'font-bold text-gray-900' : 'text-gray-600'}`}><Rich text={c.text} /></span>
                </li>
              );
            })}
          </ol>
        )}
        {note && <p className="text-[13px] leading-snug text-gray-500">{note}<Cite keys={noteSrc} sources={sources} /></p>}
      </div>
    </section>
  );
}

// THE SIMPLE TABLE: your situation against what to choose. Two columns (three
// at most), short words, six rows at most.
//   table: { title, head: [a, b], rows: [[a, b]], note?, src? }
export function SimpleTable({ id = 'table', table, sources }) {
  const { title, head, rows, note, src = [] } = table;
  return (
    <Section id={id} title={title}>
      <div className="mt-3 overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full border-collapse text-[15px] sm:text-[16px]">
          <thead>
            <tr className="bg-gray-50 text-left text-[13px] text-gray-600">
              {head.map((h) => <th key={h} scope="col" className="border-b border-gray-200 px-3 py-2.5 font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={r[0]}>
                {r.map((c, i) => (i === 0
                  ? <th key={i} scope="row" className={`px-3 py-2.5 text-left align-top font-semibold leading-snug text-gray-800 ${ri < rows.length - 1 ? 'border-b border-gray-100' : ''}`}>{c}</th>
                  : <td key={i} className={`px-3 py-2.5 align-top font-extrabold leading-snug text-gray-900 tabular-nums ${ri < rows.length - 1 ? 'border-b border-gray-100' : ''}`}>{c}</td>))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(note || src.length > 0) && <p className="mt-2 text-[15px] leading-snug text-gray-700"><Rich text={note} /><Cite keys={src} sources={sources} /></p>}
    </Section>
  );
}

// THE FLOOR x HOURS GRID (Saheb, 18 Oct 2026): one word in each cell, a
// one-line rule under it. Fits a 400 px phone: short heads, times listed
// under the grid.
//   grid: { title, cols: [{ short, note }], rows: [{ label, short, cells: [{ head, tone }] }], rule, src }
export function AnswerGrid({ id = 'grid', grid, sources }) {
  const TONE = { gray: 'bg-gray-50', green: 'bg-green-50', blue: 'bg-blue-50' };
  return (
    <Section id={id} title={grid.title}>
      <table className="mt-3 w-full table-fixed border-separate border-spacing-[3px] sm:border-spacing-1.5">
        <thead>
          <tr>
            <th scope="col" className="w-[18%]"><span className="sr-only">Floor</span></th>
            {grid.cols.map((c) => (
              <th key={c.short} scope="col" className="px-0.5 pb-0.5 text-center text-[12px] font-bold leading-tight text-gray-600 sm:text-[13px]">
                {c.short}
                <span className="hidden text-[12px] font-semibold text-gray-500 sm:block">{c.note}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((r) => (
            <tr key={r.label}>
              <th scope="row" className="pr-0.5 text-left align-middle text-[12px] font-bold leading-tight text-gray-900 sm:text-[14px]">
                <span className="sm:hidden">{r.short || r.label}</span><span className="hidden sm:inline">{r.label}</span>
              </th>
              {r.cells.map((c, i) => (
                <td key={grid.cols[i].short} className={`rounded-lg px-0.5 py-3 text-center align-middle sm:rounded-xl ${TONE[c.tone || 'gray']}`}>
                  <span className="block text-[13px] font-extrabold leading-tight text-gray-900 sm:text-[17px]">{c.head}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1.5 text-[12px] leading-snug text-gray-500 sm:hidden">{grid.cols.map((c) => `${c.short}: ${c.note}`).join(' · ')}</p>
      <p className="mt-2 text-[15px] leading-snug text-gray-700"><Rich text={grid.rule} /><Cite keys={grid.src} sources={sources} /></p>
    </Section>
  );
}

const Tick = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-[3px] flex-none"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);
const Cross = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" aria-hidden="true" className="mt-[3px] flex-none"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

function PointList({ items, mark, sources }) {
  return (
    <ul className="grid gap-2 text-[16px] leading-normal text-gray-700">
      {items.map((p) => (
        <li key={p.text} className="flex gap-2.5">
          {mark === 'good' ? <Tick /> : <Cross />}
          <span><Rich text={p.text} /><Cite keys={p.src} sources={sources} /></span>
        </li>
      ))}
    </ul>
  );
}

// PROS AND CONS of each choice, one card each, stacked on a phone.
//   sides: [{ key, title: 'Inverter AC: pros and cons', good: [..], bad: [..], goodHead?, badHead? }]
// A guide that is not a comparison passes one side with goodHead 'Do' and
// badHead "Don't".
export function ProsCons({ sides, sources }) {
  return sides.map((s) => (
    <Section key={s.key} id={`pc-${s.key}`} title={s.title}>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-3.5">
          <p className="mb-2 text-[13px] font-bold uppercase tracking-wide text-green-700">{s.goodHead || 'Pros'}</p>
          <PointList items={s.good} mark="good" sources={sources} />
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3.5">
          <p className="mb-2 text-[13px] font-bold uppercase tracking-wide text-red-700">{s.badHead || 'Cons'}</p>
          <PointList items={s.bad} mark="bad" sources={sources} />
        </div>
      </div>
    </Section>
  ));
}

// EXPLAINED SIMPLY: each part a plain question and two to four bullets.
// `extras` maps a part's index to a small animation or table shown under it.
//   parts: [{ id, q, bullets: [{ text, src }] }]
export function Parts({ parts, extras = {}, sources }) {
  return parts.map((p, i) => (
    <Section key={p.id} id={p.id} title={p.q}>
      <ul className="mt-3 grid gap-1.5 text-[16px] leading-normal text-gray-700">
        {p.bullets.map((b) => (
          <li key={b.text} className="grid grid-cols-[14px_minmax(0,1fr)] gap-2">
            <span aria-hidden="true" className="mt-[9px] h-1.5 w-1.5 rounded-full bg-gray-400" />
            <span><Rich text={b.text} /><Cite keys={b.src} sources={sources} /></span>
          </li>
        ))}
      </ul>
      {extras[i]}
    </Section>
  ));
}

// QUESTIONS PEOPLE ASK, each answered in one to three short sentences.
export function Questions({ items, sources }) {
  return (
    <Section id="faq" title="Questions people ask">
      <div className="mt-2 grid">
        {items.map((f) => (
          <div key={f.q} className="border-b border-gray-200 py-3.5 last:border-b-0">
            <h3 className="text-[17px] font-bold leading-snug text-gray-900">{f.q}</h3>
            <p className="mt-1 max-w-[66ch] text-[16px] leading-normal text-gray-600"><Rich text={f.a} /><Cite keys={f.src} sources={sources} /></p>
          </div>
        ))}
      </div>
    </Section>
  );
}

// The white card the one tool at the bottom of a guide sits in.
export function ToolCard({ id = 'tool', title, children }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className={`mt-9 scroll-mt-20 overflow-hidden ${CARD}`}>
      <h2 id={`${id}-h`} className="border-b border-gray-200 bg-gray-50 px-4 py-3 text-[18px] font-extrabold text-gray-900 sm:text-[20px]">{title}</h2>
      <div className="grid gap-4 p-4">{children}</div>
    </section>
  );
}

// Big buttons for a choice: at least 44 px tall.
export function Choice({ label, options, value, onChange }) {
  return (
    <div className="grid gap-1.5">
      <p className="text-[14px] font-semibold text-gray-700">{label}</p>
      <div role="group" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={String(o.value)} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}
            className={`min-h-[44px] rounded-xl border px-4 text-[15px] font-bold transition ${value === o.value ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-800 hover:border-blue-300'}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Line icons for the hub and series cards, by the `icon` key in data/guides.js.
const ICON_PATHS = {
  inverter: <><rect x="3" y="4" width="18" height="7" rx="2" /><path d="M7 15c1.2 1.2-1.2 2.8 0 4M12 15c1.2 1.2-1.2 2.8 0 4M17 15c1.2 1.2-1.2 2.8 0 4" /></>,
  meter: <><rect x="4" y="3" width="16" height="18" rx="3" /><rect x="7" y="6" width="10" height="5" rx="1" /><circle cx="16" cy="16" r="1.4" /><path d="M8 16h4" /></>,
  star: <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" />,
  thermometer: <path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0z" />,
  vrv: <><rect x="3" y="14" width="8" height="7" rx="1.5" /><path d="M7 14V9h10V4M17 9v5M13 18h4a2 2 0 0 0 2-2v-2" /><rect x="14" y="3" width="7" height="3" rx="1" /></>,
  service: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z" />,
  rupee: <path d="M7 4h10M7 8h10M7 4c5 0 7 1.5 7 4s-2 4-7 4l8 8" />,
  ruler: <><rect x="2" y="8" width="20" height="8" rx="1.5" /><path d="M6 8v3M10 8v4M14 8v3M18 8v4" /></>,
  chip: <><rect x="6" y="6" width="12" height="12" rx="2" /><path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" /></>,
  calculator: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 7h8M8 11h2M12 11h2M8 15h2M12 15h2M8 18h8" /></>,
};
const BOOK = <path d="M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2zM18 19v2H6" />;

export function GuideIcon({ name, tone = 'blue', size = 'h-11 w-11' }) {
  const box = tone === 'green' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700';
  return (
    <span className={`grid ${size} shrink-0 place-items-center rounded-xl ${box}`} aria-hidden="true">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        {ICON_PATHS[name] || BOOK}
      </svg>
    </span>
  );
}

export function GuideSources({ sources }) {
  return (
    <Section id="sources" title="Sources">
      <ol className="mt-3 grid list-decimal gap-2 pl-6 text-sm text-gray-600">
        {sources.map((s, i) => (
          <li key={s.key} id={`src-${i + 1}`} className="scroll-mt-24 rounded target:bg-blue-50">
            {s.href
              ? (s.href.startsWith('/')
                ? <Link to={s.href} className="text-blue-700 hover:text-blue-800">{s.label}</Link>
                : <a href={s.href} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-800">{s.label}</a>)
              : <span className="text-gray-800">{s.label}</span>}
            <span className="ml-1.5 text-[12px] font-bold uppercase tracking-wide text-gray-500">{s.by}</span>
          </li>
        ))}
      </ol>
    </Section>
  );
}

export function GuideSeries({ current }) {
  const others = GUIDES.filter((g) => g.slug !== current);
  return (
    <Section id="more" title="More AC questions, answered">
      <div className="mt-3.5 grid gap-2.5 sm:grid-cols-2">
        {GUIDE_TOOLS.map((t) => (
          <Link key={t.href} to={t.href} className="grid grid-cols-[44px_minmax(0,1fr)] gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3 hover:border-blue-300">
            <GuideIcon name={t.icon || 'calculator'} tone="green" />
            <span className="grid gap-1">
              <span className="text-[12px] font-bold uppercase tracking-wide text-green-700">Live tool</span>
              <span className="text-[15px] font-bold leading-snug text-gray-900">{t.title}</span>
              <span className="text-[13px] text-gray-600">{t.blurb}</span>
            </span>
          </Link>
        ))}
        {others.map((g) => (g.live ? (
          <Link key={g.slug} to={guidePath(g)} className="grid grid-cols-[44px_minmax(0,1fr)] gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3 hover:border-blue-300">
            <GuideIcon name={g.icon} />
            <span className="grid gap-1">
              <span className="text-[12px] font-bold uppercase tracking-wide text-blue-700">Guide</span>
              <span className="text-[15px] font-bold leading-snug text-gray-900">{g.title}</span>
              <span className="text-[13px] text-gray-600">{g.blurb}</span>
            </span>
          </Link>
        ) : (
          <div key={g.title} className="grid gap-1 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3.5 py-3">
            <span className="text-[12px] font-bold uppercase tracking-wide text-gray-500">Coming next</span>
            <span className="text-[15px] font-bold leading-snug text-gray-900">{g.title}</span>
            <span className="text-[13px] text-gray-600">{g.blurb}</span>
          </div>
        )))}
      </div>
    </Section>
  );
}

// Guides are reading pages, so a call is welcome here (unlike the shop).
export function GuideCta({ source, title = 'Still not sure which one fits your room?' }) {
  return (
    <section aria-labelledby="cta-h" className="mt-9 grid gap-3 rounded-2xl bg-gray-900 px-5 py-5 text-white">
      <h2 id="cta-h" className="text-[22px] font-extrabold text-white">{title}</h2>
      <p className="text-[15px] text-gray-300">Talk to our sales team, or book a 30-minute video call with an engineer for ₹99, adjusted against your order.</p>
      <p className="select-all text-xl font-extrabold tracking-wide tabular-nums">77039 00307</p>
      <div className="flex flex-wrap gap-2">
        <a href="tel:+917703900307" onClick={() => track(FUNNEL.PHONE_CLICK, { source })}
          className="inline-flex min-h-[44px] items-center rounded-xl bg-white px-4 text-[15px] font-bold text-gray-900 hover:bg-blue-50">Call sales</a>
        <Link to="/hvac-consultant" className="inline-flex min-h-[44px] items-center rounded-xl px-4 text-[15px] font-bold text-white ring-[1.5px] ring-inset ring-white/35 hover:bg-white/10">₹99 video call</Link>
        <Link to="/ac-calculator" className="inline-flex min-h-[44px] items-center rounded-xl px-4 text-[15px] font-bold text-white ring-[1.5px] ring-inset ring-white/35 hover:bg-white/10">Find my AC size</Link>
      </div>
    </section>
  );
}

// IN RUPEES: one worked example (the insurers' and Atomberg's pattern).
//   example: { title, lines: [{ label, value }], text, src }
export function RupeeExample({ example, sources }) {
  return (
    <section aria-labelledby="example-h" className="mt-9 grid gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <h2 id="example-h" className="text-[19px] font-extrabold text-gray-900">{example.title}</h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {example.lines.map((l) => (
          <div key={l.label} className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-[14px] text-gray-600">{l.label}</p>
            <p className="text-[20px] font-extrabold tabular-nums text-gray-900">{l.value}</p>
          </div>
        ))}
      </div>
      <p className="text-[15px] leading-normal text-gray-700"><Rich text={example.text} /><Cite keys={example.src} sources={sources} /></p>
    </section>
  );
}

// FROM OUR SHOWROOM: Saheb's own words, with his photo.
//   tip: { text, meaning?, by, src }
export function ShowroomTip({ tip, sources }) {
  return (
    <section aria-labelledby="tip-h" className="mt-9 grid gap-3 rounded-2xl border border-gray-200 bg-white p-4">
      <h2 id="tip-h" className="text-[13px] font-bold uppercase tracking-widest text-blue-700">From our showroom</h2>
      <div className="grid grid-cols-[48px_minmax(0,1fr)] items-start gap-3">
        <img src={AUTHOR.photo} alt="" width="48" height="48" className="h-12 w-12 rounded-full bg-gray-100 object-cover" />
        <div className="grid gap-1">
          <p className="text-[19px] font-bold leading-snug text-gray-900">&ldquo;{tip.text}&rdquo;<Cite keys={tip.src} sources={sources} /></p>
          {tip.meaning && <p className="text-[16px] text-gray-700">{tip.meaning}</p>}
          <p className="text-[13px] text-gray-500">{tip.by}</p>
        </div>
      </div>
    </section>
  );
}

// WHERE TO GO NEXT for this answer: products or pages that fit it.
//   fits: [{ label, title, text, href }]
export function Fits({ fits, title = 'See it for yourself' }) {
  return (
    <Section id="fits" title={title}>
      <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
        {fits.map((f) => (
          <Link key={f.href} to={f.href} className="grid gap-1 rounded-xl border border-gray-200 bg-white px-3.5 py-3 hover:border-blue-300">
            <span className="text-[12px] font-bold uppercase tracking-wide text-blue-700">{f.label}</span>
            <span className="text-[16px] font-bold leading-snug text-gray-900">{f.title}</span>
            <span className="text-[14px] text-gray-600">{f.text}</span>
          </Link>
        ))}
      </div>
    </Section>
  );
}

// THE WHOLE PAGE BODY after the header, in the playbook's order. `c` is the
// content object from the guide's data file; `watch` the guide's animation
// (its own <Explainer>); `extras` small pieces under parts; `tool` the one
// tool, at the bottom after the questions.
export function GuideBody({ c, sources, watch, extras, tool, grid, current, ctaSource, ctaTitle }) {
  return (
    <>
      <AnswerBox answer={c.answer} because={c.because} sources={sources} />
      {watch}
      {(grid || c.grid) && <AnswerGrid grid={grid || c.grid} sources={sources} />}
      {c.table && <SimpleTable table={c.table} sources={sources} />}
      {c.example && <RupeeExample example={c.example} sources={sources} />}
      <ProsCons sides={c.sides} sources={sources} />
      {c.tip && <ShowroomTip tip={c.tip} sources={sources} />}
      <Parts parts={c.parts} extras={extras} sources={sources} />
      {c.fits && <Fits fits={c.fits} />}
      <Questions items={c.faq} sources={sources} />
      {tool}
      <GuideSources sources={sources} />
      <GuideSeries current={current} />
      <GuideCta source={ctaSource} title={ctaTitle} />
    </>
  );
}
